import React, { ReactNode, useMemo } from "react";

import { faInfoCircle } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, Spinner, Text } from "@performyard/py-components";

import Tooltip from "../../../../../../../components/Tooltip";

export const KPICard = ({
	title,
	info,
	counter,
	description,
	type = "number",
	isLoading = false,
	testId = "kpi-card",
}: {
	title: ReactNode;
	info: string;
	counter: number;
	description: string[];
	type?: "number" | "percentage";
	isLoading?: boolean;
	testId?: string;
}) => {
	const counterDisplay = useMemo(() => {
		if (type === "percentage") {
			if (counter === 0) {
				return "0";
			}
			return `${counter.toFixed(0)}%`;
		}

		const suffixes = ["", "K", "M", "B"];
		let valueCounter = counter;
		let suffixIndex = 0;
		while (valueCounter >= 1000) {
			valueCounter /= 1000;
			suffixIndex++;
		}
		let counterValue = valueCounter.toFixed(0);
		let counterAbbreviation = suffixes[suffixIndex];

		if (counterAbbreviation === undefined) {
			counterValue = counter.toExponential(0);
			counterAbbreviation = "";
		}

		return `${counterValue}${counterAbbreviation}`;
	}, [counter, type]);

	return (
		<div
			data-testid={testId}
			className="flex w-full grow flex-col gap-4 rounded-md border border-solid border-neutral-2 p-4"
		>
			<div className="flex gap-1">
				{title}
				{info && (
					<Tooltip message={info}>
						<Icon icon={faInfoCircle} size={16} color="neutral-7" />
					</Tooltip>
				)}
			</div>
			<div className="flex h-[48px] items-center gap-2">
				{isLoading ? (
					<div className="flex h-full w-full items-center justify-around">
						<Spinner size={32} />
						<span />
						<span />
					</div>
				) : (
					<>
						<div data-testid={`${testId}-counter-display`} className="text-[56px] font-bold text-neutral-9">
							{counterDisplay}
						</div>
						<div className="flex flex-col">
							<Text size="caption" weight="semi-bold">
								{description[0]}
							</Text>
							<Text size="caption" weight="semi-bold">
								{description[1]}
							</Text>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
